import { IStore } from '../../interfaces/Store';
import type { CompanyPeriodMetrics } from 'washday-sdk';
import {
  useQuery,
} from '@tanstack/react-query';
import {
  getAvailableBillingExtraPackages,
  getAvailableSubscriptionPlans,
  getCFDIAddonStatus,
  getCompanyByIdV2,
  getCompanyOrdersMetrics,
  getGarmentAttributeCatalogsV2,
} from '../../services/company.service';
import {
  AvailableBillingExtraPackagesResponse,
  AvailableSubscriptionPlansResponse,
  CFDIAddonStatus,
  GarmentAttributeCatalog,
  ICompany,
} from '../../interfaces/Company';
import { getFrontendFallbackGarmentAttributeCatalogs } from '../../utils/garmentAttributeCatalogs';

export const useGetCompanyById = (enabled: boolean, _id: string) => useQuery<ICompany>({
  queryKey: ['company', { _id: _id }],
  enabled,
  queryFn: async ({ queryKey }) => {
    try {
      const [_key, { _id }] = queryKey;
      const res = await getCompanyByIdV2(_id);
      if (!res?.data?.data) {
        throw new Error('Oh no! Something went wrong while fetching.');
      }
      return res?.data?.data;
    } catch (e: any) {
      console.log('error caught', e['response']?.data?.['errors']);
      throw e;
    }
  }
});

export const useGetCompanyOrdersMetrics = () => useQuery<CompanyPeriodMetrics>({
  queryKey: ['company', 'metrics', 'orders'],
  enabled: true,
  queryFn: async () => {
    try {
      const res = await getCompanyOrdersMetrics();
      if (!res?.data?.data) {
        throw new Error('Oh no! Something went wrong while fetching.');
      }
      return res.data.data;
    } catch (e: any) {
      console.log('error caught', e['response']?.data?.['errors']);
      throw e;
    }
  }
});

export const useGetCFDIAddonStatus = (enabled: boolean, companyId: string) =>
  useQuery<CFDIAddonStatus>({
    queryKey: ['company', 'cfdiAddonStatus', { _id: companyId }],
    enabled,
    queryFn: async () => {
      try {
        const res = await getCFDIAddonStatus({ companyId });
        if (!res?.data?.data) {
          throw new Error('Oh no! Something went wrong while fetching CFDI billing status.');
        }
        return res.data.data;
      } catch (e: any) {
        console.log('error caught', e['response']?.data?.['errors']);
        throw e;
      }
    }
  });

export const useGetAvailableSubscriptionPlans = (enabled: boolean, companyId: string) =>
  useQuery<AvailableSubscriptionPlansResponse>({
    queryKey: ['company', 'availableSubscriptionPlans', { _id: companyId }],
    enabled,
    queryFn: async () => {
      try {
        const res = await getAvailableSubscriptionPlans({ companyId });
        if (!res?.data?.data) {
          throw new Error('Oh no! Something went wrong while fetching subscription plans.');
        }
        return res.data.data;
      } catch (e: any) {
        console.log('error caught', e['response']?.data?.['errors']);
        throw e;
      }
    }
  });

export const useGetAvailableBillingExtraPackages = (enabled: boolean, companyId: string) =>
  useQuery<AvailableBillingExtraPackagesResponse>({
    queryKey: ['company', 'availableBillingExtraPackages', { _id: companyId }],
    enabled,
    queryFn: async () => {
      try {
        const res = await getAvailableBillingExtraPackages({ companyId });
        if (!res?.data?.data) {
          throw new Error('Oh no! Something went wrong while fetching billing extra packages.');
        }
        return res.data.data;
      } catch (e: any) {
        console.log('error caught', e['response']?.data?.['errors']);
        throw e;
      }
    }
  });

export const useGetGarmentAttributeCatalogs = (
  enabled: boolean,
  companyId: string,
  options?: {
    fallbackOnError?: boolean;
  },
) =>
  useQuery<GarmentAttributeCatalog[]>({
    queryKey: ['company', 'garmentAttributeCatalogs', { _id: companyId }],
    enabled,
    queryFn: async () => {
      try {
        const res = await getGarmentAttributeCatalogsV2(companyId);
        if (!res?.data?.data?.garmentAttributeCatalogs) {
          throw new Error('Oh no! Something went wrong while fetching garment attribute catalogs.');
        }
        return res.data.data.garmentAttributeCatalogs;
      } catch (e: any) {
        console.log('error caught', e['response']?.data?.['errors']);
        if (options?.fallbackOnError) {
          return getFrontendFallbackGarmentAttributeCatalogs();
        }
        throw e;
      }
    }
  });
